import React, { useState, useEffect } from 'react';
import NavigationSidebar from '../../components/navigation/NavigationSidebar';
import UserProfileDropdown from '../../components/navigation/UserProfileDropdown';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import VisaFilterPanel from './components/VisaFilterPanel';
import VisaApplicationTable from './components/VisaApplicationTable';
import ValidationWorkflowPanel from './components/ValidationWorkflowPanel';
import CriticalAlertsPanel from './components/CriticalAlertsPanel';
import BulkOperationsPanel from './components/BulkOperationsPanel';
import ComplianceTrackingPanel from './components/ComplianceTrackingPanel';
import NotificationSchedulePanel from './components/NotificationSchedulePanel';
import VisaDetailsModal from './components/VisaDetailsModal';
import { useLanguage } from '../../contexts/LanguageContext';
import { getVisaApplications, updateVisaApplication } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const VisaManagement = () => {
  const navigate = useNavigate();
  const { currentLanguage, t } = useLanguage();
  const { profile, loading: authLoading } = useAuth();
  const isRTL = currentLanguage === 'ar';
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterUrgency, setFilterUrgency] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [visaApplications, setVisaApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVisa, setSelectedVisa] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadVisaApplications();
  }, []);

  const loadVisaApplications = async () => {
    try {
      setLoading(true);
      const data = await getVisaApplications();
      setVisaApplications(data || []);
    } catch (err) {
      console.error('Error loading visa applications:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateVisaStatus = async (id, newStatus) => {
    try {
      await updateVisaApplication(id, { status: newStatus });
      await loadVisaApplications();
    } catch (err) {
      console.error('Error updating visa status:', err);
      alert(t('visas.updateError'));
    }
  };

  const mockUser = profile || {
    name: "Fatima Zahra",
    role: "travel_agent",
    agency: "Al-Baraka Travel",
    avatar: "https://randomuser.me/api/portraits/women/44.jpg"
  };

  const mockNotifications = [];

  const filteredApplications = visaApplications?.filter((app) => {
    const matchesStatus = filterStatus === 'all' || app?.status === filterStatus;
    const matchesUrgency = filterUrgency === 'all' || app?.urgency === filterUrgency;
    const matchesSearch = app?.pilgrimName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         app?.passportNumber?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesStatus && matchesUrgency && matchesSearch;
  });

  const mockPendingApprovals = [];
  const mockCriticalAlerts = [];
  const mockComplianceData = {
    nusukSync: "active",
    lastNusukSync: new Date()?.toISOString(),
    regulatoryCompliance: "compliant",
    lastComplianceCheck: new Date()?.toISOString(),
    auditTrail: "active",
    auditRecordCount: visaApplications?.length || 0,
    autoArchiving: "active",
    archivedVisaCount: 0
  };
  const mockScheduledNotifications = [];

  const filterCounts = {
    total: visaApplications?.length,
    pending: visaApplications?.filter((app) => app?.status === 'pending')?.length,
    in_review: visaApplications?.filter((app) => app?.status === 'in_review')?.length,
    approved: visaApplications?.filter((app) => app?.status === 'approved')?.length,
    rejected: visaApplications?.filter((app) => app?.status === 'rejected')?.length,
    expired: visaApplications?.filter((app) => app?.status === 'expired')?.length
  };

  const handleFilterChange = (key, value) => {
    if (key === 'status') setFilterStatus(value);
    if (key === 'urgency') setFilterUrgency(value);
    if (key === 'search') setSearchQuery(value);
  };

  const handleResetFilters = () => {
    setFilterStatus('all');
    setFilterUrgency('all');
    setSearchQuery('');
  };

  const handleSelectApplication = (application) => {
    setSelectedVisa(application);
    setIsModalOpen(true);
  };

  const handleBulkSelect = (ids) => {
    // Not implemented in this example
  };

  const handleApprove = (id) => {
    console.log('Approve application:', id);
  };

  const handleReject = (id) => {
    console.log('Reject application:', id);
  };

  const handleRequestDocuments = (id) => {
    console.log('Request documents for:', id);
  };

  const handleResolveAlert = (id) => {
    console.log('Resolve alert:', id);
  };

  const handleBulkStatusUpdate = (status) => {
    console.log('Bulk status update to:', status, 'for IDs:', []);
  };

  const handleBulkDocumentRequest = () => {
    console.log('Bulk document request for IDs:', []);
  };

  const handleBulkNotification = (template) => {
    console.log('Bulk notification:', template, 'for IDs:', []);
  };

  const handleEditSchedule = (id) => {
    console.log('Edit schedule:', id);
  };

  const handleCancelSchedule = (id) => {
    console.log('Cancel schedule:', id);
  };

  const handleLogout = () => {
    console.log('User logged out');
  };

  const handleLanguageChange = (newLanguage) => {
    console.log('Language changed to:', newLanguage);
  };

  const handleNotificationClick = (notification) => {
    console.log('Notification clicked:', notification);
  };

  const handleMarkAsRead = (id) => {
    console.log('Mark notification as read:', id);
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationSidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />

      <div className={`transition-smooth ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'}`}>
        <header className="sticky top-0 z-30 bg-card border-b border-border shadow-warm">
          <div className="flex items-center justify-between pl-16 pr-4 md:px-6 lg:px-8 py-4">
            <div className="flex-1">
              <h1 className="text-xl md:text-2xl font-bold text-foreground">
                {t('visas.pageTitle')}
              </h1>
              <p className="caption text-muted-foreground mt-1">
                {t('visas.pageSubtitle')}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <NotificationCenter
                notifications={mockNotifications}
                onNotificationClick={handleNotificationClick}
                onMarkAsRead={handleMarkAsRead}
                currentLanguage={currentLanguage} />

              <UserProfileDropdown
                user={mockUser}
                onLogout={handleLogout}
                onLanguageChange={handleLanguageChange}
                currentLanguage={currentLanguage} />

            </div>
          </div>
        </header>

        <main className="pl-4 pr-4 md:px-6 lg:px-8 py-6 md:py-8">
          <BreadcrumbNavigation currentLanguage={currentLanguage} />

          <div className="space-y-6">
            <VisaFilterPanel
              filters={{
                status: filterStatus,
                urgency: filterUrgency,
                search: searchQuery
              }}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters}
              counts={filterCounts} />


            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2 space-y-6">
                <VisaApplicationTable
                  applications={filteredApplications}
                  onSelectApplication={handleSelectApplication}
                  onBulkSelect={handleBulkSelect}
                  selectedIds={[]} />


                <ComplianceTrackingPanel complianceData={mockComplianceData} />
              </div>

              <div className="space-y-6">
                <CriticalAlertsPanel
                  alerts={mockCriticalAlerts}
                  onResolveAlert={handleResolveAlert} />


                <ValidationWorkflowPanel
                  pendingApprovals={mockPendingApprovals}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onRequestDocuments={handleRequestDocuments} />


                <BulkOperationsPanel
                  selectedCount={0}
                  onBulkStatusUpdate={handleBulkStatusUpdate}
                  onBulkDocumentRequest={handleBulkDocumentRequest}
                  onBulkNotification={handleBulkNotification} />

              </div>
            </div>

            <NotificationSchedulePanel
              scheduledNotifications={mockScheduledNotifications}
              onEditSchedule={handleEditSchedule}
              onCancelSchedule={handleCancelSchedule} />

          </div>
        </main>
      </div>

      {selectedVisa && (
        <VisaDetailsModal
          application={selectedVisa}
          onClose={() => setSelectedVisa(null)}
        />
      )}
    </div>);

};

export default VisaManagement;